/**
 * PinMarker.tsx — Draggable drop-a-pin null-renderer (Plan 02-04 / SCORE-05)
 *
 * Analog: TractLayer.tsx (same null-renderer + useRef + useEffect lifecycle pattern).
 *
 * Manages one draggable MapLibre marker:
 *  - Fires onPinMoved on dragend (with normalized longitude — T-02-15 / Pitfall 4)
 *  - Fires onPinMoved on map click (click-to-drop UX)
 *  - Syncs marker position when the location prop changes (address-search sync)
 *  - Cleans up handlers + marker on unmount (StrictMode-safe — T-02-16 / Pitfall 5)
 *
 * Returns null — operates on the WebGL canvas, contributes no DOM nodes to React.
 *
 * Color note: '#0f172a' is the one allowed hardcoded literal in Phase 2 (D-10).
 * MapLibre cannot resolve CSS variables in its `color` option.
 */
import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import type { Marker } from 'maplibre-gl'
import { useMap } from './MapContext'
import { isInteractiveFeatureClick } from './interactiveMapLayers'

export interface PinMarkerProps {
  /** Current pin location, or null when no pin has been placed yet. */
  location: { lat: number; lng: number } | null
  /**
   * Called whenever the pin is dragged to a new position or the map is clicked.
   * `method` distinguishes the gesture (T-126 pin_dropped analytics): a map click vs a
   * marker drag. The address-search sync (Effect 3) deliberately does NOT call this —
   * that path is the address_searched event, not a pin drop (double-count guard).
   */
  onPinMoved: (loc: { lat: number; lng: number }, method: 'click' | 'drag') => void
}

/**
 * Normalize longitude to [-180, 180] to guard against MapLibre's world-wrap.
 * Applied to the lng value from marker.getLngLat() before invoking onPinMoved.
 * T-02-15: server Zod is the secondary guard; this is the client-side primary.
 */
function normalizeLng(lng: number): number {
  return ((((lng + 180) % 360) + 360) % 360) - 180
}

export function PinMarker({ location, onPinMoved }: PinMarkerProps): null {
  const map = useMap()
  const markerRef = useRef<Marker | null>(null)
  // T-482: whether the marker is currently ON the map. The marker is imperative (MapLibre, not a
  // React DOM node), so React can't unmount it when `location` clears — Effect 3 must detach it
  // itself. Tracking attachment explicitly (rather than reading getElement().isConnected) keeps the
  // add/remove decision reliable and unit-testable.
  const attachedRef = useRef(false)

  // WR-02: stable ref so dragend / click handlers in Effects 1 & 2 always
  // call the latest onPinMoved without needing to re-register event listeners
  // on every MapShell re-render (which would cause a different closure to be
  // captured each time activeTract or other state changes in MapShell).
  const onPinMovedRef = useRef(onPinMoved)
  useEffect(() => {
    onPinMovedRef.current = onPinMoved
  }, [onPinMoved])

  // -------------------------------------------------------------------------
  // Effect 1: Create marker on map init; clean up on unmount (Pitfall 5)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!map) return

    // Create draggable marker — '#0f172a' literal required (D-10 exception: MapLibre
    // cannot resolve CSS vars in the color option; value = --primary in light mode).
    // WR-01: do NOT call addTo(map) here — without setLngLat MapLibre renders the
    // marker at [0,0] (null island, off the coast of West Africa). The marker is
    // added only once a real position is available (see Effect 3 below).
    const marker = new maplibregl.Marker({ draggable: true, color: '#0f172a' })
    markerRef.current = marker

    // Set aria-label on the marker element for accessibility
    const el = marker.getElement()
    el.setAttribute('aria-label', 'Drag to move score location')

    // dragend: read position, normalize lng, fire callback via stable ref (WR-02)
    function onDragEnd() {
      const { lat, lng } = marker.getLngLat()
      onPinMovedRef.current({ lat, lng: normalizeLng(lng) }, 'drag')
    }
    marker.on('dragend', onDragEnd)

    // If a location is already set when this effect runs (e.g., address-search
    // happened before the map finished loading), place the marker immediately.
    if (location) {
      marker.setLngLat([location.lng, location.lat]).addTo(map)
      attachedRef.current = true
    }

    return () => {
      marker.off('dragend', onDragEnd)
      marker.remove()
      markerRef.current = null
      attachedRef.current = false
    }
  }, [map]) // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // Effect 2: Click-to-drop — place/move marker on map click
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!map) return

    function onMapClick(e: maplibregl.MapMouseEvent) {
      // T-179: a click on an interactive feature (a POI icon/count chip/cluster, or an EV
      // charger dot/cluster) is handled by that feature's own layer (popup / zoom-expand) —
      // the site pin must NOT move. Only empty-map / tract clicks drop the pin.
      if (isInteractiveFeatureClick(map!, e.point)) return
      const { lat, lng } = e.lngLat
      markerRef.current?.setLngLat([lng, lat])
      onPinMovedRef.current({ lat, lng }, 'click') // stable ref avoids stale closure (WR-02)
    }

    map.on('click', onMapClick)
    return () => {
      map.off('click', onMapClick)
    }
  }, [map]) // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // Effect 3: Sync the marker to the external location prop (address search, a
  // pin drop, or a Back/Forward restore). This makes the marker's presence a pure
  // function of `location`, symmetric in both directions:
  //   • location set  → position it and attach it (add on first position — WR-01,
  //                     so it never renders at null island [0,0]).
  //   • location null → detach it (T-482). The marker is imperative, so clearing
  //                     pinLocation in React does NOT remove it; without this it
  //                     lingers on the canvas after a Back to a no-pin URL entry.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const m = markerRef.current
    if (!m) return
    if (!location) {
      if (attachedRef.current) {
        m.remove()
        attachedRef.current = false
      }
      return
    }
    m.setLngLat([location.lng, location.lat])
    if (!attachedRef.current) {
      m.addTo(map!)
      attachedRef.current = true
    }
  }, [location]) // eslint-disable-line react-hooks/exhaustive-deps

  return null
}
